import { z } from 'zod'

import {
  bpsSchema,
  bytesSchema,
  currencyGroupingSchema,
  isoInstantSchema,
  moneyPaisa,
  nonNegativePaisaSchema,
  positivePaisaSchema,
} from '../common'

/**
 * O3 · `GET /settings` · `PATCH /settings` (Owner Console design 06).
 *
 * These are the assumptions every figure in the book is built on: the daily
 * feed rate, the two commission rates, the expected sale costs that break-even
 * is solved against. Changing one is retroactive by construction (BR §5) —
 * that is the point of storing rates rather than posting rows — so each change
 * is audited with before/after.
 */
export const tenantSettingsResponse = z.object({
  businessName: z.string(),
  currencyGrouping: currencyGroupingSchema,
  defaultDailyRatePaisa: moneyPaisa(),
  defaultBuyCommissionBps: bpsSchema,
  defaultSellCommissionBps: bpsSchema,
  /** The fixed costs break-even is solved against, beside the commission. */
  expectedSaleCostsPaisa: moneyPaisa(),
  /** C12 · Whether a Staff account may record a sale (flagged `needsReview`). */
  staffCanRecordSale: z.boolean(),
  priceLockEnabled: z.boolean(),
  pinSet: z.boolean(),
  pinChangedAt: isoInstantSchema.nullish(),
  autoLockSeconds: z.number().int().positive(),
  biometricAllowed: z.boolean(),
  keepOriginalVideos: z.boolean(),
  seasonTagCurrent: z.string().nullish(),
  lastFullBackupAt: isoInstantSchema.nullish(),
  /**
   * **Response-only, and derived** (§4.15): what the herd is *actually* costing
   * per animal per day, from real bills. It sits beside the assumed rate so the
   * settings screen shows the gap where the assumption is made. A client cannot
   * send it — it is not a setting, it is a measurement.
   */
  actualPerAnimalPerDayPaisa: moneyPaisa(),
  storageUsedBytes: bytesSchema.optional(),
})

export const patchTenantSettingsRequest = z
  .object({
    businessName: z.string().trim().min(2).max(120).optional(),
    currencyGrouping: currencyGroupingSchema.optional(),
    defaultDailyRatePaisa: positivePaisaSchema.optional(),
    defaultBuyCommissionBps: bpsSchema.optional(),
    defaultSellCommissionBps: bpsSchema.optional(),
    expectedSaleCostsPaisa: nonNegativePaisaSchema.optional(),
    staffCanRecordSale: z.boolean().optional(),
    priceLockEnabled: z.boolean().optional(),
    autoLockSeconds: z.number().int().min(30).max(3600).optional(),
    biometricAllowed: z.boolean().optional(),
    keepOriginalVideos: z.boolean().optional(),
    seasonTagCurrent: z.string().trim().max(40).nullish(),
  })
  .refine((v) => Object.keys(v).length > 0, 'Nothing to change.')

export type TenantSettingsResponse = z.infer<typeof tenantSettingsResponse>
export type PatchTenantSettingsRequest = z.input<typeof patchTenantSettingsRequest>
